<template>
  <div class="flex flex-col font-ds">
    <div class="px-6 pt-5 pb-4 border-b border-border">
      <div class="flex items-center gap-2.5 px-3.5 py-2.5 bg-accent rounded-ds-md">
        <Quote class="size-3.5 shrink-0 text-brand" />
        <p class="flex-1 min-w-0 text-sm font-medium text-accent-foreground">
          {{ sentence }}
        </p>
        <DSButton
          variant="link"
          size="sm"
          class="shrink-0 h-auto px-0 font-bold"
          @click="emit('edit-sentence')"
        >
          <Pencil class="size-3" />
          Edit
        </DSButton>
      </div>
    </div>

    <div class="flex flex-col gap-4 p-6">
      <!--
        Announced: this arrives in answer to something the user just typed, so a
        screen reader should hear it — politely, not by interruption.
      -->
      <DSAlert
        tone="warning"
        live="polite"
      >
        <template #icon>
          <CircleAlert />
        </template>
        <DSAlertDescription class="text-sm">
          {{ message }}
        </DSAlertDescription>
        <!--
          The assistant's own questions. Shown rather than folded into the
          message because each one is a separate thing it could not determine.
        -->
        <DSAlertDescription
          v-if="questions?.length"
          class="text-sm"
        >
          <ul class="flex flex-col gap-1 mt-1.5 pl-4 list-disc">
            <li
              v-for="question in questions"
              :key="question"
            >
              {{ question }}
            </li>
          </ul>
        </DSAlertDescription>
      </DSAlert>

      <!--
        Each example is a complete sentence the assistant can read, so tapping
        one re-runs the read with it rather than dropping it into the composer.
      -->
      <div
        v-if="examples?.length"
        class="flex flex-col gap-2.5"
      >
        <h4 class="text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
          Try one of these
        </h4>
        <div class="flex flex-wrap gap-2">
          <DSButton
            v-for="example in examples"
            :key="example"
            variant="secondary"
            size="sm"
            class="border-border font-medium"
            @click="emit('example', example)"
          >
            <Sparkles class="size-3.5" />
            {{ example }}
          </DSButton>
        </div>
      </div>

      <!--
        The way out that does not depend on the assistant: pick the wizard and
        fill it in. Without this the screen is a dead end for anyone whose
        sentence it cannot read.
      -->
      <div class="flex flex-col gap-2.5">
        <h4 class="text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
          Or start a wizard yourself
        </h4>
        <div class="flex flex-wrap gap-2">
          <DSButton
            v-for="option in flows"
            :key="option"
            variant="secondary"
            size="sm"
            class="border-border font-medium"
            @click="emit('pick-flow', option)"
          >
            <component
              :is="WIZARD_BY_FLOW[option].icon"
              class="size-3.5"
            />
            {{ WIZARD_BY_FLOW[option].label }}
          </DSButton>
        </div>
      </div>
    </div>

    <div class="flex gap-2.5 px-6 py-4 bg-background border-t border-border sm:justify-end">
      <DSButton
        variant="secondary"
        @click="emit('edit-sentence')"
      >
        Edit the sentence
      </DSButton>
      <DSButton
        v-if="!examples?.length"
        @click="emit('retry')"
      >
        Try again
      </DSButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { CircleAlert, Pencil, Quote, Sparkles } from '@lucide/vue'

import { AssetWizardFlow } from '@/store/ai/assetWizard/utils/types'

import { DSAlert, DSAlertDescription, DSButton } from '@ds'

import { WIZARD_BY_FLOW, WIZARD_FLOWS } from '../utils/registry'

/*
 * The two ways a read can end without a wizard: the request failed, or the
 * sentence describes nothing the wizards cover. Both leave the user with the
 * sentence they typed and a way back to it — never with a guessed flow.
 */
const { flows = WIZARD_FLOWS } = defineProps<{
  sentence: string
  /** Why there is no wizard. The assistant's words when it has any. */
  message: string
  /** Details the assistant could not determine, one per line. */
  questions?: string[]
  /** Sentences it can read, offered instead of a retry. */
  examples?: string[]
  /** Wizards the user can start by hand. All of them, unless narrowed. */
  flows?: AssetWizardFlow[]
}>()

const emit = defineEmits<{
  (e: 'edit-sentence'): void
  (e: 'example', example: string): void
  (e: 'retry'): void
  /** Skip the assistant: open this wizard at its first step. */
  (e: 'pick-flow', flow: AssetWizardFlow): void
}>()
</script>
